import React, {useEffect, useState} from "react";
import {Layout, Breadcrumb, List, Typography, Row, Input, Tabs, Table, Tag, Tooltip, Space} from 'antd';
import Api from "../api.js";
import {Link, useNavigate, useParams} from "react-router-dom"
import moment from "moment/moment";
import { Switch } from 'antd';

const {Content} = Layout;
const {Text} = Typography;
const {Search} = Input;

const CONNECTION_FIELDS = [
    {field: 'facebook_id', label: 'Facebook'},
    {field: 'google_id', label: 'Google'},
    {field: 'apple_id', label: 'Apple'},
];

const getUserConnections = (record) =>
    CONNECTION_FIELDS.filter(({field}) => record[field]).map(({label}) => label);

const User = () => {
    const params = useParams();
    const [user, setUser] = useState('');
    const [users, setUsers] = useState([]);
    const [meta, setMeta] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [loading, setLoading] = useState(false);
    const [visibleRowKey, setVisibleRowKey] = useState(null);
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("0");
    const [onlyActiveAccess, setOnlyActiveAccess] = useState(false);
    const [totalVipRevenue, setTotalVipRevenue] = useState(0);

    // Function to handle toggle visibility
    const [sorter, setSorter] = useState({
        field: 'added',
        order: 'descend'
    });

    const handleToggleVisibility = (key) => {
        setVisibleRowKey(visibleRowKey === key ? null : key);
    };

    useEffect(() => {
        Api.get('/admin/v2/vip-revenue').then(res => {
            setTotalVipRevenue(res.data.vip_revenue_total);
        })
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        searchUser();
    }, [page, pageSize, activeTab, sorter, onlyActiveAccess]);

    const searchUser = (
        tabKey = activeTab,
        currentPage = page,
        currentPageSize = pageSize,
        currentSorter = sorter
    ) => {

        let query = `?page=${currentPage}&per_page=${currentPageSize}`;

        if (user) {
            query += `&search=${user}`;
        }

        if (tabKey && tabKey !== "0") {
            query += `&product_id=${tabKey}`;
        }

        if (onlyActiveAccess) {
            query += `&active_access=1`;
        }

        if (currentSorter?.field) {
            query += `&sort_by=${currentSorter.field}`;
            query += `&sort_dir=${currentSorter.order === 'ascend' ? 'asc' : 'desc'}`;
        }

        setLoading(true);

        Api.get('/admin/user-list' + query).then(res => {
            setMeta(res.data.meta);
            setUsers(res.data.data);
            setLoading(false);
        });
    };
    // Updated getActiveAccess
    const getActiveAccess = (record) => {
        const currentDate = new Date();
        const vipProductID = 136;
        const fitlapPlusId = [103,104, 105];
        const expiredProductID = 6;

        const isAccessActive = (access) => {
            const beginDate = new Date(access.begin_date + "T00:00:00");
            const expireDate = new Date(access.expire_date + "T23:59:59"); // include full day
            return currentDate >= beginDate && currentDate <= expireDate;
        };

        if (activeTab === 'vip') {
            return record.access?.find(val => val.product_id === vipProductID && isAccessActive(val));
        }

        if (activeTab === 'fitlap_plus') {
            return record.access?.find(val =>
                fitlapPlusId.includes(val.product_id) && isAccessActive(val)
            );
        }

        return record.access?.find(val => val.product_id !== expiredProductID && isAccessActive(val));
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'user_id',
            key: 'user_id',
            width: 60,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (value, record) => (<div><Link to={'/user/' + record.user_id}>{value}</Link></div>),
        },
        {
            title: 'Firstname',
            dataIndex: 'name_f',
            key: 'name_f',
        },
        {
            title: 'Signup',
            dataIndex: 'added',
            key: 'added',
            sorter: true,
            render: (value, record) => (<div>{moment(new Date(value)).format('DD.MM.YYYY - HH:mm')}</div>)
        },
        {
            title: 'Connections',
            key: 'connections',
            render: (_, record) => {
                const connections = getUserConnections(record);

                if (!connections.length) {
                    return null;
                }

                return (
                    <Space size={[4, 4]} wrap>
                        {connections.map((connection) => (
                            <Tag key={connection}>{connection}</Tag>
                        ))}
                    </Space>
                );
            },
        },
        (activeTab === 'vip' || activeTab === 'fitlap_plus') && {
            title: 'Family Members',
            key: 'family_members_count',
            dataIndex: 'family_members',
            sorter: true,
            render: (_, record) => {
                const count = record.family_members?.length || 0;

                return count > 0
                    ? <Tag>{count}</Tag>
                    : <Tag>0</Tag>;
            }
        },
        {
            title: 'Access',
            dataIndex: 'access',
            key: 'access',
            render: (value, record) => {
                const activeAccess = getActiveAccess(record);

                return (
                    <div>
                        {activeAccess ? (
                            <Tag color="green">Active</Tag>
                        ) : (
                            <Tag color="red">Inactive</Tag>
                        )}
                    </div>
                )
            }
        },
        {
            title: 'Product',
            dataIndex: 'product',
            key: 'product',
            sorter: true,
            render: (value, record) => {
                const activeAccess = getActiveAccess(record);

                if (!activeAccess) {
                    return <Tag>-</Tag>;
                }

                // Get product name
                const productName = activeAccess.product_id === 136 ? "VIP" : activeAccess.product_id;

                // Get billing plan ID safely
                const billingPlanId = activeAccess.invoice?.items?.[0]?.billing_plan_id ?? '';

                // Map billing plan ID to friendly name
                const billingPlanNameMap = {
                    192: 'recurring-vip-12m-kasutusajata',
                    191: 'recurring-vip-kasutusajata',
                    177: 'recurring-vip-12m',
                    175: 'recurring-vip',
                    181: 'recurring-vip-32',
                    178: 'trial-vip-14day'
                };

                const billingPlanName = billingPlanNameMap[billingPlanId] || billingPlanId;

                return (
                    <Tooltip title="Product ID - Billing plan ID">
                        <Tag>
                            {productName} - {billingPlanName}
                        </Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Price',
            key: 'price',
            dataIndex: 'access_price',
            sorter: true,
            render: (_, record) => {
                const activeAccess = getActiveAccess(record);

                if (!activeAccess) {
                    return <Tag>-</Tag>;
                }

                const matchingInvoice =  activeAccess.invoice;

                if (!matchingInvoice) {
                    return <Tag>-</Tag>;
                }

                return (
                    <div>
                        <Tag color="blue">
                            {matchingInvoice.first_total} {matchingInvoice.currency}
                        </Tag>
                    </div>
                );
            }
        },
        {
            title: 'Expires',
            dataIndex: 'expires',
            key: 'expires',
            render: (value, record) => {
                const activeAccess = getActiveAccess(record);

                return (
                    <div>
                        {activeAccess ? (
                            <Tag>
                                {moment(activeAccess.expire_date).format('DD.MM.YYYY')}
                            </Tag>
                        ) : (
                            <Tag>Expired</Tag>
                        )}
                    </div>
                )
            }
        },
        {
            title: <Space>
                Total Paid {totalVipRevenue > 0 && activeTab === 'vip' && <Tag color="purple">VIP: {totalVipRevenue}€</Tag>}
            </Space>,
            dataIndex: 'total_paid',
            key: 'total_paid',
            sorter: true,
            render: (value) => (
                <Tag color="purple">
                    {value ?? 0} €
                </Tag>
            )
        },
    ].filter(Boolean);

    return (
        <Content style={{margin: '0 16px'}}>
            <Breadcrumb style={{margin: '16px 0'}}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>User</Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <Row justify="space-between" align="middle">

                    <h1>Users ({meta ? meta.total : 0})</h1>

                    <Search placeholder="Search user" onChange={e => setUser(e.target.value)} onSearch={() => {
                        setPage(1);
                        searchUser();
                    }}
                            style={{width: 300}}/>
                </Row>

                <Tabs
                    activeKey={activeTab}
                    onChange={(key) => {
                        if (loading) return;

                        setActiveTab(key);
                        setPage(1);
                    }}
                    items={[
                        {label: "Users", key: "0"},
                        {label: <div>VIPs</div>, key: "vip"},
                        {label: <div>Fitlap+</div>, key: "fitlap_plus"},
                    ]}
                    tabBarExtraContent={
                            <div>
                                Active acccess only: <Switch
                                checked={onlyActiveAccess}
                                onChange={(checked) => {
                                    setOnlyActiveAccess(checked);
                                    setPage(1);
                                }}
                            />
                            </div>
                    }
                />

                <Table
                    rowKey="user_id"
                    loading={loading}
                    size="small"
                    columns={columns}
                    dataSource={users}
                    onChange={(pagination, filters, sorterParam) => {
                        console.log(pagination);

                        setPage(pagination.current);
                        setPageSize(pagination.pageSize);

                        if (sorterParam?.field) {
                            setSorter({
                                field: sorterParam.field,
                                order: sorterParam.order
                            });
                        }
                    }}
                    pagination={{
                        current: meta?.current_page,
                        total: meta?.total,
                        pageSize: meta?.per_page,
                        showSizeChanger: true,
                        pageSizeOptions: ['10','20','50','100']
                    }}
                />
            </div>
        </Content>
    );
}

export default User;